/**
 * PTD-M5 — Squad Orchestration Score Card
 *
 * Displays the MACRO squad score (formation + teamwork) computed at the
 * team level, distinct from individual (micro) scores shown in the leaderboard.
 *
 * Spec: backlog/positional-team-drill/EPIC.md PTD-M5
 *       docs/PLAN_POSITIONAL_TEAM_DRILL.md §13
 */
import type { SquadOrchestrationResult } from '@/services/evaluationApi';

export interface SquadScoreCardProps {
  result: SquadOrchestrationResult;
}

// ─── Score colour ─────────────────────────────────────────────────────────────

function scoreColour(score: number): string {
  if (score >= 80) return 'text-green-400';
  if (score >= 60) return 'text-amber-400';
  return 'text-red-400';
}

// ─── Sub-stat ─────────────────────────────────────────────────────────────────

interface SubStatProps {
  label: string;
  value: number | null;
  'data-testid'?: string;
}

function SubStat({ label, value, 'data-testid': testId }: SubStatProps) {
  const display =
    value !== null ? (
      <span className={`text-xl font-bold tabular-nums ${scoreColour(Math.round(value))}`}>
        {Math.round(value)}
      </span>
    ) : (
      <span className="text-xl font-bold text-text-muted" aria-label="not available">
        —
      </span>
    );

  return (
    <div className="flex flex-col items-center gap-0.5" data-testid={testId}>
      {display}
      <span className="text-xs text-text-secondary text-center">{label}</span>
    </div>
  );
}

// ─── Component ────────────────────────────────────────────────────────────────

function SquadScoreCard({ result }: SquadScoreCardProps) {
  const {
    macro_squad_score,
    ffid_score,
    fsyn_score,
    partial_formation,
    player_count_expected,
    player_count_scored,
  } = result;

  const roundedMacro = Math.round(macro_squad_score);

  return (
    <div
      className="rounded-xl border border-border-subtle bg-dark-elevated-1 overflow-hidden"
      data-testid="squad-score-card"
    >
      {/* Partial-formation warning */}
      {partial_formation && (
        <div
          className="px-4 py-2 bg-amber-500/10 border-b border-amber-500/20 flex items-center gap-2 text-xs text-amber-400"
          data-testid="partial-formation-banner"
        >
          <span className="font-semibold">Partial</span>
          <span className="text-amber-400/80">
            — only {player_count_scored} of {player_count_expected} players detected;
            formation scored on the subset.
          </span>
        </div>
      )}

      <div className="p-5">
        {/* Headline: macro squad score */}
        <div className="flex items-baseline gap-3 mb-4">
          <h2 className="text-sm font-semibold text-text-secondary uppercase tracking-wider">
            Squad Orchestration
          </h2>
          <span
            className={`text-3xl font-bold tabular-nums ${scoreColour(roundedMacro)}`}
            data-testid="squad-macro-score"
            aria-label={`Squad Orchestration score: ${roundedMacro} out of 100`}
          >
            {roundedMacro}
          </span>
          <span className="text-sm text-text-muted">/100</span>
        </div>

        {/* Divider */}
        <div className="h-px bg-border-subtle mb-4" />

        {/* Sub-stats: F-FID and F-SYN */}
        <div className="flex items-center gap-8">
          <SubStat
            label="Formation (F-FID)"
            value={ffid_score}
            data-testid="squad-ffid-score"
          />
          <div className="h-8 w-px bg-border-subtle" aria-hidden="true" />
          <SubStat
            label="Player Sync (F-SYN)"
            value={fsyn_score}
            data-testid="squad-fsyn-score"
          />
        </div>

        {/* Squad dimension note — makes clear these are team, not individual, scores */}
        <p className="mt-4 text-xs text-text-muted">
          Squad scores measure the team as a unit. Individual scores appear in the
          leaderboard below.
        </p>
      </div>
    </div>
  );
}

export default SquadScoreCard;
